/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import * as MVPActions from "Dataflows/MVP/MVPAction";
import * as RDUserProfileAction from "Dataflows/RD/UserProfile/RDUserProfileAction";

import * as Yup from "yup";

import { IPersonaProps, Stack, StackItem, TextField } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import ComboBoxController from "Components/ReactHookForm/ComboBoxController/ComboBoxController";
import DatePickerController from "Components/ReactHookForm/DatePickerController/DatePickerController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { isModernshipOptions } from "Constants/ProfileConstants";

import { IAzureBenefitAddRecordProps } from "./IAzureBenefitAddRecordFormProps";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { getAddAzureBenefitOperationStatus as getAddAzureBenefitOperationStatusMVP } from "Dataflows/MVP/Benefits/MVPBenefitsSelector";
import { getAddAzureBenefitOperationStatus as getAddAzureBenefitOperationStatusRD } from "Dataflows/RD/UserProfile/RDUserProfileSelector";
import { IMVPAzureBenefitClaim } from "Interfaces/MVP/IMVPAzureBenefitClaim";

export const AzureBenefitAddRecordForm = (props: IAzureBenefitAddRecordProps): ReactElement => {
  const {
    isPanelOpen,
    setIsPanelOpen,
    isSaving,
    errorMessage,
    addAzureBenefitCredit,
    dismissPanel,
  } = props;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        registrationId: Yup.string().required().required("Registration ID is required"),
      })
    ),
  });

  const dispatch = useDispatch();
  const addAzureBenefitStatusMVP = useSelector(getAddAzureBenefitOperationStatusMVP);
  const addAzureBenefitStatusRD = useSelector(getAddAzureBenefitOperationStatusRD);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (isPanelOpen) {
      if (addAzureBenefitStatusMVP.update === OperationStatus.Succeeded) {
        dispatch(MVPActions.fetchProfileBenefits(parseInt(id)));
      } else if (addAzureBenefitStatusRD.update === OperationStatus.Succeeded) {
        dispatch(RDUserProfileAction.getProfileBenefits(parseInt(id)));
      }
      dismissPanel();
    }
  }, [
    addAzureBenefitStatusMVP.update === OperationStatus.Succeeded,
    addAzureBenefitStatusRD.update === OperationStatus.Succeeded,
  ]);

  const save = () =>
    handleSubmit((data) => {
      return addAzureBenefitCredit(data as IMVPAzureBenefitClaim);
    });

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title="Add a Record"
        saveButtonLabel="Save"
        error={errorMessage}
        disableActions={isSaving}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <>You can add a new record via the input below</>
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <TextFieldController
              control={control}
              errors={errors}
              inputName="registrationId"
              isRequired={true}
              labelDisplayName="Registration ID"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <DatePickerController
              control={control}
              errors={errors}
              inputName="startDate"
              isRequired={true}
              labelDisplayName="Activation Date"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              control={control}
              inputName="capCredit"
              errors={errors}
              isRequired={true}
              textFieldType="number"
              labelDisplayName="Additional Credit"
              prefix="$"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <DatePickerController
              control={control}
              errors={errors}
              inputName="expirationDate"
              isRequired={true}
              labelDisplayName="Credit expiration date"
              isDisabled={isSaving}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="email"
              control={control}
              errors={errors}
              isRequired
              hasTooltip
              labelDisplayName="Email Address"
              toolTipDisplayText="Please enter the new login email."
            />
          </StackItem>
          <StackItem>
            <ComboBoxController
              control={control}
              errors={errors}
              inputName="isModernSponsorship"
              isRequired={true}
              labelDisplayName="Is Modern"
              options={isModernshipOptions}
              isDisabled={isSaving}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
